import { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { onboardingBadge } from '@/components/sales/SalesPersonsTab';
import { SalesKycView } from '@/components/sales/SalesKycView';
import { kycBadge } from '@/components/sales/SalesClientDetail';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const TH = 'text-left py-2.5 px-4 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary';

function Section({ title, children, testid }) {
  return (
    <Card className="border border-brand-border bg-white" data-testid={testid}>
      <CardContent className="p-5 space-y-3"><h3 className="text-xs uppercase tracking-widest font-semibold text-brand-text-secondary">{title}</h3>{children}</CardContent>
    </Card>
  );
}

function ReviewBar({ data, spId, reload }) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const p = data.person;
  const ready = data.kyc?.status === 'submitted' && p.exam_passed;
  const act = async (action) => {
    if (action === 'reject' && !notes.trim()) { toast.error('Please add a reason for rejection'); return; }
    setBusy(true);
    try { const r = await api.put(`/sales/persons/${spId}/review`, { action, notes }); toast.success(r.data.message); setNotes(''); reload(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Section title="Verification" testid="sp-review">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {onboardingBadge(p.onboarding_status)}
        <span>KYC: <b className="capitalize">{data.kyc?.status && data.kyc.status !== 'draft' ? data.kyc.status : 'not submitted'}</b></span>
        <span>Exam: <b>{p.exam_passed ? `Passed (${p.exam_best_score}/30)` : 'Not passed'}</b></span>
        {p.review_notes && <span className="text-brand-text-secondary">Note: {p.review_notes}</span>}
      </div>
      {p.onboarding_status !== 'verified' && (
        <div className="flex flex-wrap gap-2">
          <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (required to reject)" className="max-w-sm h-9" data-testid="sp-review-notes" />
          <Button onClick={() => act('approve')} disabled={busy || !ready} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9" data-testid="sp-approve-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <><CheckCircle size={14} className="mr-1" /> Approve</>}</Button>
          <Button onClick={() => act('reject')} disabled={busy} variant="outline" className="text-rose-600 border-rose-200 h-9" data-testid="sp-reject-btn"><XCircle size={14} className="mr-1" /> Reject</Button>
          {!ready && <span className="text-xs text-brand-text-secondary self-center">Approval needs submitted KYC and a passed exam.</span>}
        </div>
      )}
    </Section>
  );
}

export default function SalesPersonProfilePage() {
  const { spId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/sales/persons/${spId}/profile`).then(r => setData(r.data))
      .catch(err => { toast.error(formatError(err.response?.data?.detail)); navigate('/sales-team'); });
  }, [spId, navigate]);
  useEffect(() => { load(); }, [load]);
  const pay = async (id) => {
    try { await api.put(`/sales/commissions/${id}/pay`, { note: '' }); toast.success('Marked as paid'); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  if (!data) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-brand-primary" /></div>;
  const p = data.person; const t = data.totals;
  return (
    <div className="space-y-5" data-testid="sales-person-profile">
      <Link to="/sales-team" className="inline-flex items-center gap-1 text-sm text-brand-text-secondary hover:text-brand-primary" data-testid="sp-back-link"><ArrowLeft size={15} /> Sales Team</Link>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-xs text-brand-primary font-semibold">{p.sales_code}</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-brand-text-primary tracking-tight" data-testid="sp-profile-name">{p.name}</h1>
          <p className="text-sm text-brand-text-secondary">{p.email} · {p.phone || '—'} · joined {p.created_at?.slice(0, 10)} · account {p.status}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[['Clients', t.client_count], ['Client Deposits', money(t.total_deposits)], ['Client Balances', money(t.total_balance)], ['Commission Pending', money(t.commission_pending)], ['Commission Paid', money(t.commission_paid)]].map(([l, v]) => (
          <Card key={l} className="border border-brand-border bg-white"><CardContent className="p-4"><p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">{l}</p>
            <p className="font-heading text-lg font-bold" data-testid={`sp-total-${l.toLowerCase().replace(/\s/g, '-')}`}>{v}</p></CardContent></Card>
        ))}
      </div>
      <ReviewBar data={data} spId={spId} reload={load} />
      <Section title="KYC & Bank Details" testid="sp-kyc-section"><SalesKycView spId={spId} kyc={data.kyc} /></Section>
      <Section title="Exam Attempts" testid="sp-exam-section">
        {data.exam_attempts.length === 0 ? <p className="text-sm text-brand-text-secondary">No attempts yet.</p> : (
          <div className="flex flex-wrap gap-2">{data.exam_attempts.map(a => (
            <Badge key={a.id} className={`border-0 ${a.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{a.created_at.slice(0, 10)} · {a.score}/{a.total} {a.passed ? 'Pass' : 'Fail'}</Badge>
          ))}</div>)}
      </Section>
      <Section title={`Clients (${data.clients.length})`} testid="sp-clients-section">
        <div className="overflow-x-auto -mx-5"><table className="w-full text-sm">
          <thead><tr className="border-b border-brand-border bg-brand-bg/50">{['Client ID', 'Name', 'KYC', 'Deposits', 'Withdrawals', 'Profit', 'Main Balance'].map(h => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>{data.clients.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-brand-text-secondary">No clients mapped</td></tr> : data.clients.map(c => (
            <tr key={c.id} className="border-b border-brand-border/50">
              <td className="py-2.5 px-4 font-mono text-xs text-brand-primary">{c.client_code}</td>
              <td className="py-2.5 px-4"><Link to={`/clients/${c.id}`} className="font-medium hover:text-brand-primary hover:underline" data-testid={`sp-client-link-${c.id}`}>{c.name}</Link></td>
              <td className="py-2.5 px-4">{kycBadge(c.kyc_status)}</td>
              <td className="py-2.5 px-4">{money(c.deposits)}</td><td className="py-2.5 px-4">{money(c.withdrawals)}</td>
              <td className={`py-2.5 px-4 ${c.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{money(c.profit)}</td>
              <td className="py-2.5 px-4 font-semibold">{money(c.main_balance)}</td>
            </tr>))}</tbody>
        </table></div>
      </Section>
      <Section title="Commission & Payouts" testid="sp-payouts-section">
        <div className="overflow-x-auto -mx-5"><table className="w-full text-sm">
          <thead><tr className="border-b border-brand-border bg-brand-bg/50">{['Month', 'Client', 'Net Profit', 'Commission', 'Status', ''].map(h => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>{data.commissions.length === 0 ? <tr><td colSpan={6} className="py-8 text-center text-brand-text-secondary">No commissions yet</td></tr> : data.commissions.map(r => (
            <tr key={r.id} className="border-b border-brand-border/50">
              <td className="py-2.5 px-4 font-mono text-xs">{r.month}</td><td className="py-2.5 px-4">{r.client_name} <span className="font-mono text-xs text-brand-text-secondary">{r.client_code}</span></td>
              <td className="py-2.5 px-4">{money(r.client_net_profit)}</td><td className="py-2.5 px-4 font-semibold text-emerald-700">{money(r.commission)}</td>
              <td className="py-2.5 px-4"><Badge className={`border-0 text-[11px] ${r.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}{r.paid_at ? ` · ${r.paid_at.slice(0, 10)}` : ''}</Badge></td>
              <td className="py-2.5 px-4">{r.status === 'pending' && <Button size="sm" onClick={() => pay(r.id)} className="h-7 text-xs bg-emerald-600 text-white" data-testid={`sp-pay-${r.id}`}>Mark Paid</Button>}</td>
            </tr>))}</tbody>
        </table></div>
      </Section>
    </div>
  );
}
